import { useState, useEffect, useRef } from 'react';
import { Modal, Backdrop, Box, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import InfiniteScroll from 'react-infinite-scroller';

import Button from 'src/components/NewButton';
import Checkbox from 'src/components/Checkbox';
import { useClickAway } from 'src/hooks/useClickAway';
import { All_Languages } from 'src/containers/sidebar/LanguageFilter';

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      backgroundColor: '#fff',
      borderRadius: 8,
      boxShadow:
        '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
      padding: 24,
      width: 500,
      maxHeight: 600,
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      marginBottom: 16,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      border: '1px solid #D0D5DD',
      borderRadius: 8,
      marginBottom: 12,
    },
    searchInput: {
      border: 'none',
      outline: 'none',
      flex: 1,
      fontSize: 14,
    },
    options: {
      flex: 1,
      overflowY: 'auto',
      maxHeight: 400,
    },
    option: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 0',
    },
    actions: {
      display: 'flex',
      gap: 8,
      marginTop: 16,
      justifyContent: 'flex-end',
    },
  })
);

// Flatten the language tree to get all language codes
const getAllLanguageCodes = (): Array<{ id: string; name: string }> => {
  const languages: Array<{ id: string; name: string }> = [];

  const extractLanguages = (items: typeof All_Languages) => {
    items.forEach((item) => {
      if (item.children && item.children.length > 0) {
        extractLanguages(item.children);
      } else {
        languages.push({ id: item.id, name: item.name });
      }
    });
  };

  extractLanguages(All_Languages);
  return languages.sort((a, b) => a.name.localeCompare(b.name));
};

const ALL_LANGUAGES = getAllLanguageCodes();

interface LanguageSelectorProps {
  open: boolean;
  onClose: () => void;
  selectedLanguages: string[];
  onSelect: (languages: string[]) => void;
}

export const LanguageSelector = ({
  open,
  onClose,
  selectedLanguages,
  onSelect,
}: LanguageSelectorProps) => {
  const classes = useStyles();
  const [searchKey, setSearchKey] = useState('');
  const [selectedOptions, setSelectedOptions] =
    useState<string[]>(selectedLanguages);
  const [records, setRecords] = useState(100);
  const [hasMore, setHasMore] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useClickAway({ ref, handleClickAway: onClose });

  useEffect(() => {
    if (open) {
      setSearchKey('');
      setSelectedOptions(selectedLanguages);
      setRecords(100);
      setHasMore(true);
    }
  }, [open, selectedLanguages]);

  const filteredLanguages = ALL_LANGUAGES.filter(
    (item) =>
      !searchKey ||
      (item.name &&
        item.name.toLowerCase().includes(searchKey.toLowerCase())) ||
      (item.id && item.id.toLowerCase().includes(searchKey.toLowerCase()))
  );

  const loadMore = () => {
    if (records < filteredLanguages.length) {
      setRecords(Math.min(records + 100, filteredLanguages.length));
    } else {
      setHasMore(false);
    }
  };

  const toggleLanguage = (languageId: string) => {
    if (selectedOptions.includes(languageId)) {
      setSelectedOptions(selectedOptions.filter((id) => id !== languageId));
    } else {
      setSelectedOptions([...selectedOptions, languageId]);
    }
  };

  const handleApply = () => {
    onSelect(selectedOptions);
    onClose();
  };

  const handleClear = () => {
    setSelectedOptions([]);
  };

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      className={classes.modal}
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box className={classes.paper}>
        <Typography variant='h6' className={classes.header}>
          Select Languages
        </Typography>
        <Box className={classes.searchContainer}>
          <SearchIcon style={{ fontSize: 20, color: '#667085' }} />
          <input
            type='text'
            placeholder='Search languages...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className={classes.searchInput}
          />
        </Box>
        <Box className={classes.options}>
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            hasMore={hasMore}
            loader={
              <div key={0} style={{ textAlign: 'center', padding: 10 }}>
                Loading...
              </div>
            }
            useWindow={false}
            threshold={250}
          >
            {filteredLanguages.slice(0, records).map((language) => (
              <Box key={language.id} className={classes.option}>
                <Checkbox
                  name={language.id}
                  label={language.name}
                  checked={selectedOptions.includes(language.id)}
                  onChange={() => toggleLanguage(language.id)}
                />
              </Box>
            ))}
          </InfiniteScroll>
        </Box>
        <Box className={classes.actions}>
          <Button variant='outlined' height={36} onClick={handleClear}>
            Clear
          </Button>
          <Button variant='contained' height={36} onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
